import { z } from 'zod';
export const FIELD_TYPES = [
    'TEXT',
    'TEXTAREA',
    'NUMBER',
    'CURRENCY',
    'PHONE',
    'SELECT',
    'MULTI_SELECT',
    'BOOLEAN',
    'ATTACHMENT_IMAGE',
    'BRAND_SELECT',
    'BRANCH_SELECT',
];
export const fieldTypeSchema = z.enum(FIELD_TYPES);
/** Field types whose value is NOT carried in `formData`. */
export const NON_DATA_FIELD_TYPES = ['ATTACHMENT_IMAGE', 'BRAND_SELECT', 'BRANCH_SELECT'];
// ---------------------------------------------------------------------------
// Photo quality (per ATTACHMENT_IMAGE field)
// ---------------------------------------------------------------------------
/**
 * How hard the phone compresses a photo for this field. `STANDARD` is the
 * default and is enough for a storefront or general site condition; `HIGH`
 * keeps small text (serial numbers, invoices, data plates) readable.
 */
export const PHOTO_QUALITY_LEVELS = ['STANDARD', 'HIGH'];
export const photoQualitySchema = z.enum(PHOTO_QUALITY_LEVELS);
/**
 * The single source of truth for both the phone's compressor and the size hints
 * the form builder shows an admin.
 */
export const PHOTO_QUALITY_SPECS = {
    STANDARD: { longestSide: 1280, targetMinBytes: 200 * 1024, targetMaxBytes: 400 * 1024, qualitySteps: [0.72, 0.6, 0.5, 0.4] },
    HIGH: { longestSide: 1920, targetMinBytes: 500 * 1024, targetMaxBytes: 900 * 1024, qualitySteps: [0.92, 0.86, 0.8, 0.72] },
};
/**
 * Arabic capture hints seeded for new photo fields; the admin may edit them.
 * Framing matters more than resolution for readability, so a `HIGH` field is
 * seeded with a "fill the frame" instruction.
 */
export const DEFAULT_CAPTURE_HINTS = {
    STANDARD: 'صوّر الواجهة كاملة والإضاءة كويسة.',
    HIGH: 'قرّب على السيريال واملا بيه الصورة.',
};
/** Where a value goes in the Zoho Desk ticket. */
export const ZOHO_TARGETS = ['NONE', 'CUSTOM_FIELD', 'SYSTEM_FIELD'];
export const zohoTargetSchema = z.enum(ZOHO_TARGETS);
/** How the value is cast before being sent to Zoho Desk. */
export const ZOHO_VALUE_TYPES = ['STRING', 'NUMBER', 'BOOLEAN', 'DATE_TIME'];
export const zohoValueTypeSchema = z.enum(ZOHO_VALUE_TYPES);
/** Allow-list of top-level Zoho Desk ticket attributes a SYSTEM_FIELD mapping may set. */
export const ZOHO_SYSTEM_ATTRIBUTES = [
    'subject',
    'description',
    'phone',
    'email',
    'priority',
    'classification',
    'category',
    'subCategory',
    'dueDate',
    'language',
];
export const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,49}$/;
export const fieldKeySchema = z
    .string()
    .regex(FIELD_KEY_PATTERN, 'Field key must be snake_case: lowercase letters, digits and underscores, starting with a letter');
export const ZOHO_CUSTOM_FIELD_PATTERN = /^cf_[A-Za-z0-9_]+$/;
export const CHARACTER_RESTRICTIONS = ['ALL', 'NUMERIC_ONLY', 'ALPHANUMERIC', 'NO_SPECIAL_CHARS'];
export const CURRENCY_UNITS = ['EGP', 'USD', 'EUR', 'SAR'];
function isCompilableRegex(source) {
    try {
        new RegExp(source, 'u');
        return true;
    }
    catch {
        return false;
    }
}
/**
 * A quantifier applied to a group that itself repeats (star height > 1, e.g.
 * `(a+)*`) is the shape behind catastrophic backtracking; these patterns run
 * against agent-typed answers, so they are rejected outright. Conservative:
 * a safe pattern may be refused, no nested-quantifier pattern is accepted.
 */
function hasNestedQuantifier(source) {
    const groups = []; // per open group: a repeat quantifier was seen inside
    let closedGroupRepeats = false; // the group that just closed contains a repeat
    for (let i = 0; i < source.length; i++) {
        const ch = source[i];
        if (ch === '\\') {
            i += 1;
            closedGroupRepeats = false;
            continue;
        }
        if (ch === '[') {
            i += 1;
            if (source[i] === ']')
                i += 1; // a leading `]` is a literal
            while (i < source.length && source[i] !== ']')
                i += source[i] === '\\' ? 2 : 1;
            closedGroupRepeats = false;
            continue;
        }
        if (ch === '(') {
            groups.push(false);
            closedGroupRepeats = false;
            continue;
        }
        if (ch === ')') {
            closedGroupRepeats = groups.pop() ?? false;
            if (closedGroupRepeats && groups.length > 0)
                groups[groups.length - 1] = true;
            continue;
        }
        // `?` (0 or 1) cannot blow up; only unbounded or counted repeats matter.
        const isRepeat = ch === '*' || ch === '+' || (ch === '{' && /^\{\d+(,\d*)?\}/.test(source.slice(i)));
        if (isRepeat) {
            if (closedGroupRepeats)
                return true;
            if (groups.length > 0)
                groups[groups.length - 1] = true;
        }
        closedGroupRepeats = false;
    }
    return false;
}
export const validationRulesSchema = z
    .object({
    minLength: z.number().int().min(0).max(10000).nullable(),
    maxLength: z.number().int().min(1).max(10000).nullable(),
    exactLength: z.number().int().min(1).max(10000).nullable(),
    min: z.number().finite().nullable(),
    max: z.number().finite().nullable(),
    pattern: z
        .string()
        .max(200)
        .refine(isCompilableRegex, 'Invalid regular expression')
        .refine((s) => !hasNestedQuantifier(s), 'Pattern repeats a repeated group, which can hang validation')
        .nullable(),
    characterRestriction: z.enum(CHARACTER_RESTRICTIONS).nullable(),
    currencyUnit: z.enum(CURRENCY_UNITS).nullable(),
    /** For MULTI_SELECT and ATTACHMENT_IMAGE. */
    minItems: z.number().int().min(0).max(50).nullable(),
    maxItems: z.number().int().min(1).max(50).nullable(),
    errorMessage: z.string().max(200).nullable(),
})
    .partial()
    .strict();
/**
 * One choice. `value` is the stable key: it is what is stored, validated, mapped
 * to Zoho and reported on, so a label can be renamed later without breaking old
 * visits. `label` is Arabic (always present, the fallback); `labelEn` is optional.
 */
export const fieldOptionSchema = z.object({
    label: z.string().trim().min(1).max(100),
    value: z.string().trim().min(1).max(100),
    labelEn: z.string().trim().max(100).nullish(),
});
/** The option's label in `language`: English when set and asked for, Arabic otherwise. */
export function optionLabel(option, language) {
    return language.startsWith('en') && option.labelEn ? option.labelEn : option.label;
}
// ---------------------------------------------------------------------------
// Trips: branch-level fields and per-project blocks
// ---------------------------------------------------------------------------
/**
 * VISIT: asked once per trip (merchant, branch, branch manager...).
 * PROJECT: part of the project block, asked once for the main visit and again
 * for every additional project visit in the same trip.
 */
export const FIELD_SCOPES = ['VISIT', 'PROJECT'];
export const fieldScopeSchema = z.enum(FIELD_SCOPES);
/**
 * What a project-block field means, so reporting, the visits table and the
 * aggregate Zoho mappings can find it whatever its key or label. Each role is
 * used at most once per form.
 */
export const FIELD_ROLES = ['PROJECT', 'VISIT_TYPE', 'SERVICE', 'SERVICE_DETAIL', 'ISSUE_FLAG', 'ISSUE_TYPE', 'ISSUES', 'COMMENT'];
export const fieldRoleSchema = z.enum(FIELD_ROLES);
/** Field types each role accepts. */
export const FIELD_ROLE_TYPES = {
    PROJECT: ['SELECT'],
    VISIT_TYPE: ['SELECT'],
    SERVICE: ['SELECT'],
    SERVICE_DETAIL: ['SELECT', 'MULTI_SELECT'],
    ISSUE_FLAG: ['BOOLEAN'],
    ISSUE_TYPE: ['SELECT'],
    ISSUES: ['MULTI_SELECT', 'SELECT'],
    COMMENT: ['TEXT', 'TEXTAREA'],
};
/** How a yes/no field is shown: two choices, or a toggle that is off until switched on. */
export const BOOLEAN_STYLES = ['CHOICE', 'TOGGLE'];
/**
 * A dependent select: the option chosen in the parent field decides which of
 * this field's options are offered. Stored as parent option key -> this field's
 * option keys, so one option (e.g. "machine not usable") can belong to several
 * parents and still be one key for reporting.
 */
export const dependentOptionsSchema = z.object({
    fieldKey: fieldKeySchema,
    optionsByParent: z.record(z.string().max(100), z.array(z.string().max(100)).max(200)),
});
export const VISIBILITY_OPERATORS = ['EQUALS', 'NOT_EQUALS', 'IN', 'NOT_EMPTY', 'EMPTY'];
/** A field is visible only when ALL of its conditions hold. */
export const visibilityConditionSchema = z.object({
    fieldKey: fieldKeySchema,
    operator: z.enum(VISIBILITY_OPERATORS),
    value: z
        .union([z.string(), z.number(), z.boolean(), z.array(z.string())])
        .nullable()
        .optional(),
});
export const formFieldSchema = z.object({
    fieldKey: fieldKeySchema,
    /** Arabic label: always required, and the fallback everywhere. */
    label: z.string().trim().min(1).max(120),
    /** Optional English label, shown when the interface is in English. */
    labelEn: z.string().trim().max(120).nullable().default(null),
    fieldType: fieldTypeSchema,
    isRequired: z.boolean().default(false),
    displayOrder: z.number().int().min(0).max(10000),
    placeholder: z.string().max(200).nullable().default(null),
    helpText: z.string().max(500).nullable().default(null),
    options: z.array(fieldOptionSchema).max(200).nullable().default(null),
    validationRules: validationRulesSchema.nullable().default(null),
    visibleWhen: z.array(visibilityConditionSchema).max(10).nullable().default(null),
    multiSelectDelimiter: z.string().min(1).max(5).nullable().default(null),
    /** ATTACHMENT_IMAGE only; ignored (and left at the default) for every other type. */
    photoQuality: photoQualitySchema.default('STANDARD'),
    /** Shown to the agent above the camera button. Null = no hint. */
    captureHint: z.string().trim().max(200).nullable().default(null),
    zohoTarget: zohoTargetSchema.default('NONE'),
    zohoApiFieldName: z.string().trim().min(1).max(100).nullable().default(null),
    zohoValueType: zohoValueTypeSchema.default('STRING'),
    /** Asked once per trip (VISIT) or once per project block (PROJECT). Older versions are all VISIT. */
    scope: fieldScopeSchema.default('VISIT'),
    /** Reporting meaning of a project-block field; null for everything else. */
    role: fieldRoleSchema.nullable().default(null),
    /** SELECT / MULTI_SELECT only: options offered depend on an earlier single-choice field. */
    dependsOn: dependentOptionsSchema.nullable().default(null),
    /**
     * SELECT only: filled from an earlier single-choice field with the option of the
     * same key (e.g. issue type from service). The agent may change it.
     */
    prefillFrom: fieldKeySchema.nullable().default(null),
    /** BOOLEAN only. */
    booleanStyle: z.enum(BOOLEAN_STYLES).default('CHOICE'),
});
/** The label to show in `language`: English when set and asked for, Arabic otherwise. */
export function fieldLabel(field, language) {
    return language.startsWith('en') && field.labelEn ? field.labelEn : field.label;
}
/**
 * A new field that already passes the schema. Builders must never create a
 * blank field and rely on `parse()`: the label is required, so parsing a blank
 * one throws — which is exactly how "Add field" went dead (FIXES_REPORT.md §1).
 */
export function createBlankField(fieldKey, displayOrder, label) {
    return {
        fieldKey,
        label,
        labelEn: null,
        fieldType: 'TEXT',
        isRequired: false,
        displayOrder,
        placeholder: null,
        helpText: null,
        options: null,
        validationRules: null,
        visibleWhen: null,
        multiSelectDelimiter: null,
        photoQuality: 'STANDARD',
        captureHint: null,
        zohoTarget: 'NONE',
        zohoApiFieldName: null,
        zohoValueType: 'STRING',
        scope: 'VISIT',
        role: null,
        dependsOn: null,
        prefillFrom: null,
        booleanStyle: 'CHOICE',
    };
}
/** Project-block fields, in display order. */
export function projectScopeFields(fields) {
    return sortFields(fields.filter((f) => f.scope === 'PROJECT'));
}
/** Branch-level fields (asked once per trip), in display order. */
export function visitScopeFields(fields) {
    return sortFields(fields.filter((f) => f.scope !== 'PROJECT'));
}
/** True when the form has a project block, i.e. a trip may cover several projects. */
export function hasProjectBlocks(fields) {
    return fields.some((f) => f.scope === 'PROJECT');
}
export function fieldWithRole(fields, role) {
    return fields.find((f) => f.role === role);
}
export function isToggleField(field) {
    return field.fieldType === 'BOOLEAN' && field.booleanStyle === 'TOGGLE';
}
/**
 * Fields where the agent may type an entry that is not in the list ("غير موجود
 * بالقائمة", reviewed later): every multi-select field (D62) and the Project
 * field of a project block, which is a single choice (one project per block).
 */
export function acceptsTypedEntries(field) {
    return field.fieldType === 'MULTI_SELECT' || (field.fieldType === 'SELECT' && field.role === 'PROJECT');
}
/**
 * Structural rules for a form definition. Used when saving a draft (reported as
 * issues) and enforced strictly on publish.
 */
export function collectFormDefinitionIssues(fields) {
    const issues = [];
    const keyIndex = new Map();
    const zohoNames = new Map();
    const roles = new Map();
    // Walk in display order so "depends on an earlier field" matches what agents see.
    const ordered = fields
        .map((field, index) => ({ field, index }))
        .sort((a, b) => a.field.displayOrder - b.field.displayOrder || a.index - b.index);
    ordered.forEach(({ field, index }) => {
        const at = (...rest) => ['fields', index, ...rest];
        if (keyIndex.has(field.fieldKey)) {
            issues.push({ path: at('fieldKey'), message: `Duplicate field key "${field.fieldKey}"` });
        }
        else {
            keyIndex.set(field.fieldKey, index);
        }
        if (field.fieldType === 'SELECT' || field.fieldType === 'MULTI_SELECT') {
            if (!field.options || field.options.length === 0) {
                issues.push({ path: at('options'), message: 'Select fields need at least one option' });
            }
            else if (new Set(field.options.map((o) => o.value)).size !== field.options.length) {
                issues.push({ path: at('options'), message: 'Option values must be unique' });
            }
        }
        const rules = field.validationRules;
        if (rules) {
            if (rules.minLength != null && rules.maxLength != null && rules.minLength > rules.maxLength) {
                issues.push({ path: at('validationRules', 'minLength'), message: 'minLength must be <= maxLength' });
            }
            if (rules.min != null && rules.max != null && rules.min > rules.max) {
                issues.push({ path: at('validationRules', 'min'), message: 'min must be <= max' });
            }
            if (rules.minItems != null && rules.maxItems != null && rules.minItems > rules.maxItems) {
                issues.push({ path: at('validationRules', 'minItems'), message: 'minItems must be <= maxItems' });
            }
        }
        if (field.fieldType === 'ATTACHMENT_IMAGE' && field.zohoTarget !== 'NONE') {
            issues.push({
                path: at('zohoTarget'),
                message: 'Photo fields are uploaded as ticket attachments and cannot map to a ticket field',
            });
        }
        if (field.zohoTarget === 'NONE') {
            if (field.zohoApiFieldName) {
                issues.push({ path: at('zohoApiFieldName'), message: 'Remove the Zoho field name or choose a Zoho target' });
            }
        }
        else if (!field.zohoApiFieldName) {
            issues.push({ path: at('zohoApiFieldName'), message: 'Zoho API field name is required for this target' });
        }
        else {
            if (field.zohoTarget === 'CUSTOM_FIELD' && !ZOHO_CUSTOM_FIELD_PATTERN.test(field.zohoApiFieldName)) {
                issues.push({ path: at('zohoApiFieldName'), message: 'Custom field API names start with "cf_"' });
            }
            if (field.zohoTarget === 'SYSTEM_FIELD' && !ZOHO_SYSTEM_ATTRIBUTES.includes(field.zohoApiFieldName)) {
                issues.push({
                    path: at('zohoApiFieldName'),
                    message: `System field must be one of: ${ZOHO_SYSTEM_ATTRIBUTES.join(', ')}`,
                });
            }
            const previous = zohoNames.get(field.zohoApiFieldName);
            if (previous !== undefined) {
                issues.push({
                    path: at('zohoApiFieldName'),
                    message: `Zoho field "${field.zohoApiFieldName}" is already mapped by "${fields[previous]?.fieldKey}"`,
                });
            }
            else {
                zohoNames.set(field.zohoApiFieldName, index);
            }
        }
        field.visibleWhen?.forEach((condition, conditionIndex) => {
            const refIndex = keyIndex.get(condition.fieldKey);
            if (condition.fieldKey === field.fieldKey) {
                issues.push({ path: at('visibleWhen', conditionIndex), message: 'A field cannot depend on itself' });
            }
            else if (refIndex === undefined) {
                issues.push({
                    path: at('visibleWhen', conditionIndex),
                    message: `Condition refers to "${condition.fieldKey}", which must be an earlier field`,
                });
            }
            else {
                const refType = fields[refIndex]?.fieldType;
                if (refType && NON_DATA_FIELD_TYPES.includes(refType)) {
                    issues.push({
                        path: at('visibleWhen', conditionIndex),
                        message: 'Conditions can only refer to fields that hold a value (not photos, merchant or branch)',
                    });
                }
            }
            if ((condition.operator === 'EQUALS' || condition.operator === 'NOT_EQUALS') && condition.value == null) {
                issues.push({ path: at('visibleWhen', conditionIndex), message: 'A value is required for this operator' });
            }
            if (condition.operator === 'IN' && !Array.isArray(condition.value)) {
                issues.push({ path: at('visibleWhen', conditionIndex), message: 'IN needs a list of values' });
            }
            const refField = refIndex === undefined ? undefined : fields[refIndex];
            if (refField && field.scope !== 'PROJECT' && refField.scope === 'PROJECT') {
                issues.push({
                    path: at('visibleWhen', conditionIndex),
                    message: 'A branch-level field cannot depend on a per-project field (which project would it follow?)',
                });
            }
        });
        // Per-project blocks: roles and scope.
        if (field.scope === 'PROJECT' && (field.fieldType === 'BRAND_SELECT' || field.fieldType === 'BRANCH_SELECT')) {
            issues.push({ path: at('scope'), message: 'Merchant and Branch are asked once per trip; they cannot be per-project' });
        }
        if (field.role) {
            if (field.scope !== 'PROJECT') {
                issues.push({ path: at('role'), message: 'A reporting role is only for per-project fields' });
            }
            if (!FIELD_ROLE_TYPES[field.role].includes(field.fieldType)) {
                issues.push({
                    path: at('role'),
                    message: `The ${field.role} role needs a field of type ${FIELD_ROLE_TYPES[field.role].join(' or ')}`,
                });
            }
            const holder = roles.get(field.role);
            if (holder !== undefined) {
                issues.push({ path: at('role'), message: `The ${field.role} role is already used by "${fields[holder]?.fieldKey}"` });
            }
            else {
                roles.set(field.role, index);
            }
        }
        if (field.booleanStyle === 'TOGGLE' && field.fieldType !== 'BOOLEAN') {
            issues.push({ path: at('booleanStyle'), message: 'Only yes/no fields can be shown as a toggle' });
        }
        // Dependent select: options follow an earlier single-choice field.
        if (field.dependsOn) {
            const parentIndex = keyIndex.get(field.dependsOn.fieldKey);
            const parent = parentIndex === undefined ? undefined : fields[parentIndex];
            if (field.fieldType !== 'SELECT' && field.fieldType !== 'MULTI_SELECT') {
                issues.push({ path: at('dependsOn'), message: 'Only choice fields can depend on another field' });
            }
            else if (field.dependsOn.fieldKey === field.fieldKey) {
                issues.push({ path: at('dependsOn'), message: 'A field cannot depend on itself' });
            }
            else if (!parent) {
                issues.push({ path: at('dependsOn'), message: `Depends on "${field.dependsOn.fieldKey}", which must be an earlier field` });
            }
            else if (parent.fieldType !== 'SELECT') {
                issues.push({ path: at('dependsOn'), message: 'The parent of a dependent field must be a single-choice field' });
            }
            else {
                if (field.scope !== 'PROJECT' && parent.scope === 'PROJECT') {
                    issues.push({ path: at('dependsOn'), message: 'A branch-level field cannot depend on a per-project field' });
                }
                const parentValues = new Set((parent.options ?? []).map((o) => o.value));
                const ownValues = new Set((field.options ?? []).map((o) => o.value));
                for (const [parentValue, childValues] of Object.entries(field.dependsOn.optionsByParent)) {
                    if (!parentValues.has(parentValue)) {
                        issues.push({ path: at('dependsOn'), message: `"${parentValue}" is not an option of "${parent.fieldKey}"` });
                    }
                    for (const child of childValues) {
                        if (!ownValues.has(child)) {
                            issues.push({ path: at('dependsOn'), message: `"${child}" (under "${parentValue}") is not an option of this field` });
                        }
                    }
                }
                if (field.isRequired) {
                    const empty = (parent.options ?? []).filter((o) => (field.dependsOn.optionsByParent[o.value] ?? []).length === 0);
                    if (empty.length > 0) {
                        issues.push({
                            path: at('dependsOn'),
                            message: `Required, but no options for: ${empty.map((o) => o.label).join(', ')}`,
                        });
                    }
                }
            }
        }
        // Auto-fill: follows an earlier single-choice field by option key.
        if (field.prefillFrom) {
            const sourceIndex = keyIndex.get(field.prefillFrom);
            const source = sourceIndex === undefined ? undefined : fields[sourceIndex];
            if (field.fieldType !== 'SELECT') {
                issues.push({ path: at('prefillFrom'), message: 'Only single-choice fields can be auto-filled' });
            }
            else if (field.prefillFrom === field.fieldKey) {
                issues.push({ path: at('prefillFrom'), message: 'A field cannot be auto-filled from itself' });
            }
            else if (!source) {
                issues.push({ path: at('prefillFrom'), message: `Auto-filled from "${field.prefillFrom}", which must be an earlier field` });
            }
            else if (source.fieldType !== 'SELECT') {
                issues.push({ path: at('prefillFrom'), message: 'Auto-fill needs a single-choice source field' });
            }
            else if (field.scope !== 'PROJECT' && source.scope === 'PROJECT') {
                issues.push({ path: at('prefillFrom'), message: 'A branch-level field cannot be auto-filled from a per-project field' });
            }
            else {
                const own = new Set((field.options ?? []).map((o) => o.value));
                if (!(source.options ?? []).some((o) => own.has(o.value))) {
                    issues.push({
                        path: at('prefillFrom'),
                        message: `No option of "${source.fieldKey}" has the same key as an option of this field, so nothing would be filled`,
                    });
                }
            }
        }
    });
    if (fields.some((f) => f.scope === 'PROJECT') && !roles.has('PROJECT')) {
        issues.push({
            path: ['fields'],
            message: 'Per-project fields need one Project field (single choice, role Project) to name each project block',
        });
    }
    const brandFields = fields.filter((f) => f.fieldType === 'BRAND_SELECT');
    const branchFields = fields.filter((f) => f.fieldType === 'BRANCH_SELECT');
    if (brandFields.length > 1)
        issues.push({ path: ['fields'], message: 'Only one Merchant field is allowed' });
    if (branchFields.length > 1)
        issues.push({ path: ['fields'], message: 'Only one Branch field is allowed' });
    if (branchFields.length === 1 && brandFields.length === 0) {
        issues.push({ path: ['fields'], message: 'A Branch field requires a Merchant field' });
    }
    for (const f of [...brandFields, ...branchFields]) {
        if (f.visibleWhen && f.visibleWhen.length > 0) {
            issues.push({ path: ['fields'], message: 'Merchant and Branch fields cannot be conditional' });
        }
    }
    return issues;
}
/** Fields must be ordered by displayOrder for dependency checks; this sorts a copy. */
export function sortFields(fields) {
    return [...fields].sort((a, b) => a.displayOrder - b.displayOrder);
}
export const formDefinitionInputSchema = z.object({
    name: z.string().trim().min(1).max(120),
    subjectTemplate: z.string().max(250).nullable().default(null),
    fields: z.array(formFieldSchema).max(100),
});
/** Draft saves accept an incomplete definition; structural issues are returned, not rejected. */
export const saveFormDraftRequestSchema = formDefinitionInputSchema;
export const userRefSchema = z.object({ id: z.string(), fullName: z.string() });
export const formDraftDtoSchema = z.object({
    name: z.string(),
    subjectTemplate: z.string().nullable(),
    fields: z.array(formFieldSchema),
    basedOnVersion: z.number().int().nullable(),
    updatedAt: z.string(),
    updatedBy: userRefSchema.nullable(),
    issues: z.array(z.object({ path: z.array(z.union([z.string(), z.number()])), message: z.string() })),
});
export const formVersionDtoSchema = z.object({
    id: z.string(),
    version: z.number().int(),
    name: z.string(),
    subjectTemplate: z.string().nullable(),
    isActive: z.boolean(),
    publishedAt: z.string(),
    publishedBy: userRefSchema.nullable(),
    fields: z.array(formFieldSchema),
});
export const formVersionSummarySchema = formVersionDtoSchema.omit({ fields: true }).extend({
    fieldCount: z.number().int(),
    visitCount: z.number().int(),
});
export const publishFormResponseSchema = z.object({
    version: formVersionDtoSchema,
    warnings: z.array(z.string()),
});
/** Placeholders available in the subject template besides field keys. */
export const TEMPLATE_BUILTIN_KEYS = [
    'agentName',
    'brandName',
    'branchName',
    'startTime',
    'endTime',
    'visitDate',
    'durationMinutes',
];
//# sourceMappingURL=forms.js.map